import { IItemRightsCollection, RightType } from "../../../Global/Base/Rights/RightTypes";
import { ObjectUtils } from "../../../Global/Base/Utils/ObjectUtils";
import { IClassItemRights } from "../../../OW.Remoting/Remoting/Rights";
import { IControlWithXGAction, IInteractive } from "../../CommonUIInterfaces";
import { IEnabledVisualControl } from "../../UI.BaseControls/ControlInterfaces";
import { IAction, IMultiObjectAction } from "../../UI.Controls/Actions/IAction";
import { ComponentRightsExtractor } from "../Rights/ComponentRightsExtractor";
import { RightTypeEvaluationFunctionType } from "../Rights/Definitions";
import {
    IAttrNameControl,
    ICloseFormControl,
    IConfirmIfControl,
    IControl,
    IDigitalSignControl,
    IEditableControl,
    IEncryptionControl,
    IGenericControl,
    IMethodControl,
    IMultiEdit,
    IOpenAsEnabledControl,
    IRequirableControl,
    IStaticControl,
    IValidateIfControl,
    IStopRightsEvaluatingOnChildren,
    ICanIgnoreRights,
} from "../UI/Controls/ControlInterfaces";
import { StandardAction } from "../UI/XGControls/Actions/StandardAction";
import { ComponentType } from "./ComponentTree";
import { ILogicalComponent } from "./ILogicalComponent";
import { IUseMasterObject } from "Global/Base/Rights/IUseMasterObject";

export class ControlWrapper implements ILogicalComponent {
    private readonly _control: IGenericControl;
    private readonly _parentControlWrapper: ILogicalComponent;
    private readonly _attrName: string = "";
    private readonly _methodName: string = "";
    private readonly _componentType: ComponentType | null;
    private readonly _condition: string;
    private readonly _isStatic: boolean | null;
    private readonly _useMasterObject: boolean;
    private readonly _stopRightsEvaluatingOnChildren: boolean;

    public readonly isVirtualControl: boolean;

    private readonly _rightsCollection: IItemRightsCollection;
    private _classItemRights: IClassItemRights;
    private readonly _rightsIgnored: boolean;
    private _rightsEvaluationOverrides: RightTypeEvaluationFunctionType[];

    /**
     * Definice propustí, kdy se pro definované typy práv při kontrole na typ komponenty v metodě _getRightTypesMapForControl nejdříve zkontroluje, zda se nejedná o zde definovaný typ.
     * Pokud ano, projde se přes definovanou property na vnořený objekt na kterém se teprve typ komponenty bude kontrolovat a případně se dále tomu objektu bude nastavovat hodnota.
     */
    private static _passthroughDefinitions: IControlTypePassthrough<any>[] = [
        { rightType: RightType.useDigitalSign, typeToCheck: IControlWithXGAction, accessorProperty: "xgAction" },
        { rightType: RightType.useDecryption, typeToCheck: IControlWithXGAction, accessorProperty: "xgAction" },
        { rightType: RightType.useEncryption, typeToCheck: IControlWithXGAction, accessorProperty: "xgAction" },
    ];

    /**
     * Definice dvojic, který typ práva ukazuje na kterou property daného typu komponenty
     */
    private static _rightTypesToControlPropertiesMap: Map<RightType, keyof IControl> = new Map([
        [RightType.visible, "visible" as keyof IControl],
        [RightType.enabled, "enabled" as keyof IControl]]);

    private static _rightTypesToIFramePropertiesMap: Map<RightType, keyof IInteractive> = new Map([
        [RightType.interactive, "interactive" as keyof IInteractive]]);

    private static _rightTypesToIEnabledVisualControlPropertiesMap: Map<RightType, keyof IEnabledVisualControl> = new Map([
        [RightType.enabledVisual, "enabledVisual" as keyof IEnabledVisualControl]]);

    private static _rightTypesToStandardActionPropertiesMap: Map<RightType, keyof StandardAction> = new Map([
        [RightType.editing, "visible" as keyof StandardAction]]);

    private static _rightTypesToEditableControlPropertiesMap: Map<RightType, keyof IEditableControl> = new Map([
        [RightType.interactive, "interactive" as keyof IEditableControl],
        [RightType.editing, "isEditing" as keyof IEditableControl],
        [RightType.multiEditing, "multiEditActive" as keyof IEditableControl],
        [RightType.required, "required" as keyof IEditableControl],
        [RightType.postponedRequired, "postponedRequired" as keyof IEditableControl],
        [RightType.recommended, "recommended" as keyof IEditableControl]]);

    private static _rightTypesToRequirableControlPropertiesMap: Map<RightType, keyof IRequirableControl> = new Map([
        [RightType.required, "required" as keyof IRequirableControl]]);

    private static _rightTypesToEncryptionPropertiesMap: Map<RightType, keyof IEncryptionControl> = new Map([
        [RightType.useDecryption, "useDecryption" as keyof IEncryptionControl],
        [RightType.useEncryption, "useEncryption" as keyof IEncryptionControl]]);

    private static _rightTypesToCloseFormPropertiesMap: Map<RightType, keyof ICloseFormControl> = new Map([
        [RightType.closeForm, "closeForm" as keyof ICloseFormControl]]);

    private static _rightTypesToConfirmIfPropertiesMap: Map<RightType, keyof IConfirmIfControl> = new Map([
        [RightType.confirmIf, "confirmIf" as keyof IConfirmIfControl]]);

    private static _rightTypesToOpenAsEnabledPropertiesMap: Map<RightType, keyof IOpenAsEnabledControl> = new Map([
        [RightType.openAsEnabled, "openAsEnabled" as keyof IOpenAsEnabledControl]]);

    private static _rightTypesToDigitalSignPropertiesMap: Map<RightType, keyof IDigitalSignControl> = new Map([
        [RightType.useDigitalSign, "useDigitalSign" as keyof IDigitalSignControl]]);

    private static _rightTypesToValidateIfPropertiesMap: Map<RightType, keyof IValidateIfControl> = new Map([
        [RightType.validateIf, "validateIf" as keyof IValidateIfControl]]);

    private static _rightTypesToIActionPropertiesMap: Map<RightType, keyof IAction> = new Map([
        [RightType.insert, "enabled" as keyof IAction],
        [RightType.edit, "enabled" as keyof IAction],
        [RightType.delete, "enabled" as keyof IAction]]);

    private static _rightTypesToIMultiEditPropertiesMap: Map<RightType, keyof IMultiEdit> = new Map([
        [RightType.multiEditing, "multiEditActive" as keyof IMultiEdit]]);

    // POZOR na posloupnost typů, záleží na pořadí.
    /**
     * Dvojce typů komponent a jim odpovídajících map typů práv a jejich properties
     */
    private static _controlsRightTypesMap: Map<new (...params: any[]) => any/*TODO-rites: extractnout do typu někam ven? už je definováno i v IRightsEvaluationOverrides.ts*/, Map<RightType, PropertyKey>> = new Map<new (...params: any[]) => any/*TODO-rites: jinej typ?*/, Map<RightType, PropertyKey>>([
        [IAction, ControlWrapper._rightTypesToIActionPropertiesMap],
        [IEncryptionControl, ControlWrapper._rightTypesToEncryptionPropertiesMap],
        [ICloseFormControl, ControlWrapper._rightTypesToCloseFormPropertiesMap],
        [IConfirmIfControl, ControlWrapper._rightTypesToConfirmIfPropertiesMap],
        [IOpenAsEnabledControl, ControlWrapper._rightTypesToOpenAsEnabledPropertiesMap],
        [IDigitalSignControl, ControlWrapper._rightTypesToDigitalSignPropertiesMap],
        [IValidateIfControl, ControlWrapper._rightTypesToValidateIfPropertiesMap],
        [IRequirableControl, ControlWrapper._rightTypesToRequirableControlPropertiesMap],
        [IEditableControl, ControlWrapper._rightTypesToEditableControlPropertiesMap],
        [StandardAction, ControlWrapper._rightTypesToStandardActionPropertiesMap],
        [IInteractive, ControlWrapper._rightTypesToIFramePropertiesMap],
        [IEnabledVisualControl, ControlWrapper._rightTypesToIEnabledVisualControlPropertiesMap],
        [IGenericControl, ControlWrapper._rightTypesToControlPropertiesMap],
        [IMultiEdit, ControlWrapper._rightTypesToIMultiEditPropertiesMap],
    ]);

    constructor(control: IGenericControl, parentControlWrapper: ILogicalComponent, rightsIgnored: boolean = false, isVirtualControl?: boolean) {
        if (/*control instanceof IAction && */control instanceof IMultiObjectAction && control.mainAction instanceof IGenericControl) { // Do wrapperu se přidá obalovaná akce TODO-rites: ?? jak se bude přistupovat k vyhodnocování práv na MultiObjectActionách? Bude se k nim přistupovat přes menu? Jak je to vlastně teď?
            control = control.mainAction;
        }

        if (parentControlWrapper)
            this._parentControlWrapper = parentControlWrapper;

        if (isVirtualControl)
            this.isVirtualControl = true;

        if (control instanceof IAttrNameControl) {
            this._attrName = control.attrName;
            this._componentType = ComponentType.attributeComponent;
        } else if (control instanceof IMethodControl) {
            this._methodName = control.methodName;
            this._componentType = ComponentType.methodComponent;
        }

        this._control = control;

        if (control instanceof IStaticControl)
            this._isStatic = control.isStatic;

        if (control instanceof IUseMasterObject)
            this._useMasterObject = control.useMasterObject;

        if (control instanceof IStopRightsEvaluatingOnChildren)
            this._stopRightsEvaluatingOnChildren = control.stopRightsEvaluatingOnChildren ?? false;

        if (control instanceof ICanIgnoreRights && control.ignoreRights)
            rightsIgnored = true;

        this._rightsIgnored = rightsIgnored;

        if (!rightsIgnored) {
            if ((control as any).condition) ///*(control instanceof GenericListFrame /*TODO-rites: ??? jakej typ? udělat interfacem? IControlWithCondition*/)*/
                this._condition = (control as any).condition;

            this._rightsCollection = ComponentRightsExtractor.loadRightsForTypes(control);
        }
    }

    public get control(): IGenericControl {
        return this._control;
    }

    public get parentControlWrapper(): ILogicalComponent {
        return this._parentControlWrapper;
    }

    public get attrName(): string {
        return this._attrName;
    }

    public get methodName(): string {
        return this._methodName;
    }

    public get componentType(): ComponentType | null {
        return this._componentType;
    }

    public get condition(): string {
        return this._condition;
    }

    public get isStatic(): boolean | null {
        return this._isStatic;
    }

    public get useMasterObject(): boolean {
        return this._useMasterObject;
    }

    public get stopRightsEvaluatingOnChildren(): boolean {
        return this._stopRightsEvaluatingOnChildren;
    }

    public get rightsCollection(): IItemRightsCollection {
        return this._rightsCollection;
    }

    public get classItemRights(): IClassItemRights | null {
        return this._classItemRights;
    }

    public set classItemRights(value: IClassItemRights) {
        this._classItemRights = value;
    }

    public get rightsEvaluationOverrides(): RightTypeEvaluationFunctionType[] {
        return this._rightsEvaluationOverrides;
    }

    public set rightsEvaluationOverrides(value: RightTypeEvaluationFunctionType[]) {
        this._rightsEvaluationOverrides = value;
    }

    public get rightsIgnored(): boolean {
        return this._rightsIgnored;
    }

    /**
     * Pokud existuje definice propusti pro předaný typ práva, vrací vnořený objekt/komponentu na základě definice. Jinak vrací hlavní komponentu tohoto wrapperu.
     * Viz definici _passthroughDefinitions.
     * @param rightType Typ práva pro které se má kontrolovat a případně vracet
     */
    private _checkPassthroughAndGetControl(rightType: RightType): IGenericControl {
        const passthroughsForRightType = ControlWrapper._passthroughDefinitions.filter(passthroughItem => passthroughItem.rightType === rightType);

        for (const passthroughItem of passthroughsForRightType) {
            if (this.control instanceof passthroughItem.typeToCheck && Reflect.has(this.control, passthroughItem.accessorProperty)) {
                return (this.control as any)[passthroughItem.accessorProperty];
            }
        }

        return this.control;
    }

    /**
     * Zjistí, jestli má smysl vyhodnocovat zadané právo pro obalenou komponentu.
     */
    public isEvaluableForRight(rightType: RightType): boolean {
        return !!this._getRightTypesMapForControl(rightType);
    }

    /**
     * Vrací mapu příslušných názvů atributů zaregistrovaných typů komponent pro typy práv, které těmto komponentám lze nastavit/číst
     * @param rightType
     */
    private _getRightTypesMapForControl(rightType: RightType): IRightTypesMapForControlResult | null {
        for (const [controlType, rightTypesMap] of ControlWrapper._controlsRightTypesMap) {
            const control = this._checkPassthroughAndGetControl(rightType);
            if (control instanceof controlType) {
                if (rightTypesMap.has(rightType))
                    return { controlToUse: control, rightTypesMap: rightTypesMap };
            }
        }

        //this._logger.warn(`Missing definition of property accessor pair for control type "${this.control.constructor.name}" and right type "${RightType[rightType]}"`); // ` TODO-rites: debug
        return null;
    }

    /**
     * Vrací hodnotu daného typu práva na komponentě
     * @param rightType
     */
    public getRight(rightType: RightType): boolean | null {
        const rightTypesMapForControlResult = this._getRightTypesMapForControl(rightType);

        if (rightTypesMapForControlResult && rightTypesMapForControlResult.rightTypesMap) {
            return this._dynamicPropertyGet(rightTypesMapForControlResult.controlToUse, rightType, rightTypesMapForControlResult.rightTypesMap);
        }

        return null;
    }

    /**
     * Nastavuje hodnotu daného typu práva na komponentě
     * @param rightType
     * @param value
     * @returns Vrací true, pokud se podařilo hodnotu nastavit; jinak false
     */
    public setRight(rightType: RightType, value: boolean): boolean {
        const rightTypesMapForControlResult = this._getRightTypesMapForControl(rightType);

        if (rightTypesMapForControlResult && rightTypesMapForControlResult.rightTypesMap) {
            if (this._dynamicPropertySet(rightTypesMapForControlResult.controlToUse, rightType, rightTypesMapForControlResult.rightTypesMap, value))
                return true;
        }

        return false;
    }

    /**
     * Dynamické získání hodnoty práva na komponentě na základě předaného typu práva
     * @param control
     * @param rightType
     * @param rightTypesMap
     */
    private _dynamicPropertyGet<T extends IGenericControl>(control: T, rightType: RightType, rightTypesMap: Map<RightType, PropertyKey>): boolean | null {
        const propertyKey: PropertyKey = rightTypesMap.get(rightType);
        if (!propertyKey)
            return null;

        return ObjectUtils.dynamicGetter(control, propertyKey);
    }

    /**
     * Dynamické nastavení hodnoty práva na komponentě na základě předaného typu práva
     * @param control
     * @param rightType
     * @param rightTypesMap
     * @param valueToSet
     */
    private _dynamicPropertySet<T extends IGenericControl>(control: T, rightType: RightType, rightTypesMap: Map<RightType, PropertyKey>, valueToSet: boolean): boolean {
        const propertyKey: PropertyKey = rightTypesMap.get(rightType);
        if (!propertyKey)
            return false;
        return ObjectUtils.dynamicSetter(control, propertyKey, valueToSet);
    }
}

/**
 * Definice propusti pro kontrolu typu komponenty
 * @param rightType Typ práva, pro který se má propusť kontrolovat
 * @param typeToCheck Typ, na který má komponent kontrolovat
 * @param accessorProperty Property z níž se má použít objekt/komponenta
 */
interface IControlTypePassthrough<T> {
    rightType: RightType;
    typeToCheck: T;
    accessorProperty: keyof T;
}

interface IRightTypesMapForControlResult {
    controlToUse?: IGenericControl;
    rightTypesMap?: Map<RightType, PropertyKey>;
}
